/**
 * API Service
 * 
 * Centralized API client for communicating with the runtime backend.
 * Handles authentication, RBAC, and error handling.
 */

import { auth } from '../config/firebase';
import { mfaTokenStore } from './mfaTokenStore';
import { API_URL } from '@/config/env';

// API Configuration — Firebase Functions `api` for this environment
const API_BASE_URL = API_URL;

/**
 * API Error class
 */
export class APIError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * Get Firebase ID token for API authentication
 */
async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  
  try {
    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

/**
 * Make an authenticated API request
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  organizationId?: string,
  impersonatedOrgId?: string
): Promise<T> {
  const token = await getAuthToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (organizationId) {
    headers['X-Organization-Id'] = organizationId;
  }
  
  if (impersonatedOrgId) {
    headers['X-Impersonated-Org'] = impersonatedOrgId;
  }

  // Attach the short-lived second-factor proof (when the session has one) so
  // MFA-required orgs accept the request.
  const mfaToken = mfaTokenStore.get();
  if (mfaToken) {
    headers['X-MFA-Token'] = mfaToken;
  }

  // Separate proof for security-settings changes — see mfaTokenStore. Only set
  // while a fresh step-up window is open; the session token can't stand in.
  const sudoToken = mfaTokenStore.getSudo();
  if (sudoToken) {
    headers['X-Sudo-Token'] = sudoToken;
  }

  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: 'Unknown error',
        message: response.statusText
      }));

      // The active org now requires a second factor and this session hasn't
      // satisfied it — surface the gate.
      if (response.status === 403 && error.error === 'mfa_required') {
        mfaTokenStore.triggerRequired();
      }

      throw new APIError(
        error.message || 'API request failed',
        response.status,
        error.error,
        error
      );
    }
    
    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    
    console.error('API request error:', error);
    throw new APIError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

/**
 * Make an authenticated API request that returns raw bytes (file downloads).
 * Same auth/org headers as apiRequest, but resolves to a Blob.
 */
export async function apiRequestBlob(
  endpoint: string,
  organizationId?: string
): Promise<Blob> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (organizationId) headers['X-Organization-Id'] = organizationId;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, { headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: 'Unknown error',
      message: response.statusText,
    }));
    throw new APIError(error.message || 'API request failed', response.status, error.error, error);
  }
  return await response.blob();
}

// ============================================================================
// USER API
// ============================================================================

export interface UserProfile {
  id: string;
  email: string;
  displayName: string | null;
  photoURL?: string | null;
  systemRole: 'super_admin' | 'support' | null;
  organizations: Array<{
    organizationId: string;
    role: 'owner' | 'admin' | 'member' | 'viewer';
    joinedAt: Date;
  }>;
  currentOrganizationId?: string;
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
}

export async function getCurrentUser(): Promise<UserProfile> {
  return apiRequest<UserProfile>('/users/me');
}

export async function switchOrganization(organizationId: string): Promise<void> {
  await apiRequest('/users/me/organization', {
    method: 'PUT',
    body: JSON.stringify({ organizationId }),
  });
}

// ============================================================================
// ORGANIZATIONS API
// ============================================================================

export interface Organization {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  settings?: Record<string, any>;
}

export interface OrganizationMember {
  userId: string;
  email: string;
  displayName: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joinedAt: Date;
}

export async function getOrganizations(): Promise<{ organizations: Organization[]; count: number }> {
  return apiRequest<{ organizations: Organization[]; count: number }>('/organizations');
}

export async function getOrganization(organizationId: string): Promise<Organization> {
  return apiRequest<Organization>(`/organizations/${organizationId}`);
}

export async function createOrganization(name: string, ownerId: string): Promise<{ id: string; name: string }> {
  return apiRequest<{ id: string; name: string }>('/organizations', {
    method: 'POST',
    body: JSON.stringify({ name, ownerId }),
  });
}

export async function updateOrganization(
  organizationId: string,
  updates: Partial<Organization>
): Promise<void> {
  await apiRequest(`/organizations/${organizationId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteOrganization(organizationId: string): Promise<void> {
  await apiRequest(`/organizations/${organizationId}`, {
    method: 'DELETE',
  });
}

export async function getOrganizationMembers(
  organizationId: string
): Promise<{ members: OrganizationMember[]; count: number }> {
  return apiRequest<{ members: OrganizationMember[]; count: number }>(
    `/organizations/${organizationId}/members`
  );
}

export async function addOrganizationMember(
  organizationId: string,
  userId: string,
  role: 'admin' | 'member' | 'viewer'
): Promise<void> {
  await apiRequest(`/organizations/${organizationId}/members`, {
    method: 'POST',
    body: JSON.stringify({ userId, role }),
  });
}

export async function updateOrganizationMemberRole(
  organizationId: string,
  userId: string,
  role: 'admin' | 'member' | 'viewer'
): Promise<void> {
  await apiRequest(`/organizations/${organizationId}/members/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

export async function removeOrganizationMember(
  organizationId: string,
  userId: string
): Promise<void> {
  await apiRequest(`/organizations/${organizationId}/members/${userId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// PRIMITIVES API
// ============================================================================

export interface Primitive {
  id: string;
  name: string;
  description: string;
  category: string;
  type: 'primitive';
  nodes: any[];
  edges: any[];
  inputSchema?: any;
  outputSchema?: any;
  isPublished: boolean;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  version: number;
}

export async function getPrimitives(category?: string): Promise<{ primitives: Primitive[]; count: number }> {
  const params = category ? `?category=${encodeURIComponent(category)}` : '';
  return apiRequest<{ primitives: Primitive[]; count: number }>(`/primitives${params}`);
}

export async function getPrimitive(primitiveId: string): Promise<Primitive> {
  return apiRequest<Primitive>(`/primitives/${primitiveId}`);
}

export async function createPrimitive(primitive: Omit<Primitive, 'id' | 'createdBy' | 'createdAt' | 'updatedAt' | 'version'>): Promise<{ id: string }> {
  return apiRequest<{ id: string }>('/primitives', {
    method: 'POST',
    body: JSON.stringify(primitive),
  });
}

export async function updatePrimitive(primitiveId: string, updates: Partial<Primitive>): Promise<void> {
  await apiRequest(`/primitives/${primitiveId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deletePrimitive(primitiveId: string): Promise<void> {
  await apiRequest(`/primitives/${primitiveId}`, {
    method: 'DELETE',
  });
}

export async function publishPrimitive(primitiveId: string, isPublished: boolean): Promise<void> {
  await apiRequest(`/primitives/${primitiveId}/publish`, {
    method: 'POST',
    body: JSON.stringify({ isPublished }),
  });
}

export async function getPrimitiveUsage(primitiveId: string): Promise<{ primitiveId: string; usageCount: number; scenarios: any[] }> {
  return apiRequest<{ primitiveId: string; usageCount: number; scenarios: any[] }>(
    `/primitives/${primitiveId}/usage`
  );
}

// ============================================================================
// AUDIT LOGS API
// ============================================================================

export interface AuditLog {
  id: string;
  timestamp: Date;
  actorId: string;
  actorEmail: string;
  actorSystemRole: 'super_admin' | 'support' | null;
  action: string;
  resource: string;
  resourceType: string;
  organizationId?: string;
  impersonatedOrgId?: string;
  before?: any;
  after?: any;
  changes?: string[];
  success: boolean;
  errorMessage?: string;
}

export async function getSystemAuditLogs(params?: {
  actorId?: string;
  action?: string;
  resourceType?: string;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
}): Promise<{ logs: AuditLog[]; count: number }> {
  const queryParams = new URLSearchParams();
  if (params?.actorId) queryParams.append('actorId', params.actorId);
  if (params?.action) queryParams.append('action', params.action);
  if (params?.resourceType) queryParams.append('resourceType', params.resourceType);
  if (params?.startDate) queryParams.append('startDate', params.startDate.toISOString());
  if (params?.endDate) queryParams.append('endDate', params.endDate.toISOString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  
  const query = queryParams.toString();
  return apiRequest<{ logs: AuditLog[]; count: number }>(
    `/audit-logs/system${query ? `?${query}` : ''}`
  );
}

export async function getOrganizationAuditLogs(
  organizationId: string,
  params?: {
    actorId?: string;
    action?: string;
    resourceType?: string;
    startDate?: Date;
    endDate?: Date;
    limit?: number;
  }
): Promise<{ logs: AuditLog[]; count: number; organizationId: string }> {
  const queryParams = new URLSearchParams();
  if (params?.actorId) queryParams.append('actorId', params.actorId);
  if (params?.action) queryParams.append('action', params.action);
  if (params?.resourceType) queryParams.append('resourceType', params.resourceType);
  if (params?.startDate) queryParams.append('startDate', params.startDate.toISOString());
  if (params?.endDate) queryParams.append('endDate', params.endDate.toISOString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  
  const query = queryParams.toString();
  return apiRequest<{ logs: AuditLog[]; count: number; organizationId: string }>(
    `/audit-logs/organization/${organizationId}${query ? `?${query}` : ''}`
  );
}

export async function getResourceAuditLogs(
  resource: string,
  limit?: number
): Promise<{ logs: AuditLog[]; count: number; resource: string }> {
  const query = limit ? `?limit=${limit}` : '';
  return apiRequest<{ logs: AuditLog[]; count: number; resource: string }>(
    `/audit-logs/resource/${encodeURIComponent(resource)}${query}`
  );
}

export async function getImpersonationHistory(params?: {
  actorId?: string;
  organizationId?: string;
  startDate?: Date;
  endDate?: Date;
}): Promise<{ logs: AuditLog[]; count: number }> {
  const queryParams = new URLSearchParams();
  if (params?.actorId) queryParams.append('actorId', params.actorId);
  if (params?.organizationId) queryParams.append('organizationId', params.organizationId);
  if (params?.startDate) queryParams.append('startDate', params.startDate.toISOString());
  if (params?.endDate) queryParams.append('endDate', params.endDate.toISOString());
  
  const query = queryParams.toString();
  return apiRequest<{ logs: AuditLog[]; count: number }>(
    `/audit-logs/impersonation${query ? `?${query}` : ''}`
  );
}

export async function getOrganizationAuditStats(
  organizationId: string,
  startDate?: Date,
  endDate?: Date
): Promise<{
  stats: {
    totalActions: number;
    actionCounts: Record<string, number>;
    userCounts: Record<string, number>;
  };
  organizationId: string;
}> {
  const queryParams = new URLSearchParams();
  if (startDate) queryParams.append('startDate', startDate.toISOString());
  if (endDate) queryParams.append('endDate', endDate.toISOString());
  
  const query = queryParams.toString();
  return apiRequest(
    `/audit-logs/stats/${organizationId}${query ? `?${query}` : ''}`
  );
}

